import React, { useEffect, useRef } from 'react'
import Router from './Router'
import api from './api'
import { useSelector } from './store'
import 'motorsport-ui-kit/dist/motorsport-ui-kit.cjs.development.css'

function App() {
  const isAuth = useSelector((state) => state.profile.isAuth)
  const initialPathRef = useRef<string | null>(null)

  // Save the initial path on first mount (before any redirects)
  useEffect(() => {
    if (initialPathRef.current === null) {
      const currentPath = window.location.pathname
      // Don't save /login as intended path
      if (currentPath !== '/login') {
        initialPathRef.current = currentPath
        sessionStorage.setItem('intendedPath', currentPath)
      }
    }
  }, [])

  // Always check for Django session first (if user logged in via Django admin)
  // This uses cookies for session-based authentication
  // This runs on every mount to catch Django admin sessions
  const {
    isSuccess: sessionQuerySuccess,
    isLoading: sessionLoading,
    data: sessionData,
  } = api.useCheckSessionAuthQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  // Check if session check actually returned user data (not just error)
  const sessionHasUser = sessionData && typeof sessionData === 'object' && !('error' in sessionData)
  const sessionSuccess = sessionQuerySuccess && sessionHasUser

  // Check for session-based auth (if user logged in via React Admin)
  // Run if session check didn't return user data - this checks Django session via /user endpoint
  // Both endpoints check the same Django session cookie, so we only need one to succeed
  const { isLoading: getUserLoading, isSuccess: getUserSuccess } = api.useGetUserQuery(undefined, {
    skip: sessionSuccess, // Skip if session check already returned user data
    refetchOnMountOrArgChange: true, // Always check on mount to catch existing sessions
  })

  // Wait for initial auth checks to complete before rendering Router
  // This prevents redirecting to login before session check completes
  // Show loading if:
  // - We're not authenticated AND
  // - Neither check has succeeded yet AND
  // - At least one check is still loading OR neither check has finished
  const hasAuthCheckSucceeded = sessionSuccess || getUserSuccess
  const isAnyCheckLoading = sessionLoading || getUserLoading
  const hasAnyCheckFinished = !sessionLoading || !getUserLoading
  const isInitializing = !isAuth && !hasAuthCheckSucceeded && (isAnyCheckLoading || !hasAnyCheckFinished)

  if (isInitializing) {
    // Show loading state while checking authentication
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>
    )
  }

  return <Router />
}

export default App
